<template>
  <section class="materials-tab">
    <QueryErrorNotice :error="materialsTabErrors" />

    <EmptyStateNotice v-if="!materials.length && !isPending" :text="t('emptyTasks')" variant="info" />

    <div v-else class="materials-list">
      <article v-for="(item, index) in materials" :key="`material-${item.id || index}`" class="material-item">
        <button class="acc-item without-outline" type="button" @click="toggleItem(index)">
          <div class="d-flex align-items-center w-100 text-left">
            <span class="material-icons mr-2 icon">{{ getIcon(item.type) }}</span>
            <p class="f-16 mb-0 collapse-title">{{ item.name }}</p>
          </div>
          <div class="acc-icon" :class="{ 'is-open': isOpen(index) }">
            <span class="material-icons">keyboard_arrow_down</span>
          </div>
        </button>

        <div v-if="isOpen(index)" class="material-body p-3">
          <template v-if="Number(item.type) === 1">
            <FileFilter
              v-if="externalLink(item)"
              :url="externalLink(item).url"
              :name="externalLink(item).name"
              :file="externalLink(item).file"
              @open-file-viewer="openFrame"
            />
            <FileFilter
              v-if="materialFile(item)"
              :url="materialFile(item).url"
              :name="materialFile(item).name"
              :file="materialFile(item).file"
              @open-file-viewer="openFrame"
            />
          </template>

          <template v-else-if="Number(item.type) === 2">
            <div v-if="videoInfo(item)" class="mb-3">
              <iframe
                v-if="videoInfo(item).provider === 'youtube'"
                :src="`https://www.youtube.com/embed/${videoInfo(item).id}`"
                title="youtube"
                allowfullscreen
              />
              <iframe
                v-else-if="videoInfo(item).provider === 'vimeo'"
                :src="`https://player.vimeo.com/video/${videoInfo(item).id}`"
                title="vimeo"
                allowfullscreen
              />
            </div>
            <h3 v-if="item.description" class="bold f-13 mb-2">{{ t('anotation') }}</h3>
            <div v-if="item.description" class="f-13" v-html="item.description" />
          </template>

          <template v-else-if="Number(item.type) === 3">
            <FileFilter
              v-if="materialFile(item)"
              :url="materialFile(item).url"
              :name="materialFile(item).name"
              :file="materialFile(item).file"
              @open-file-viewer="openFrame"
            />
          </template>

          <template v-else-if="Number(item.type) === 5">
            <div class="conference-item">
              <div class="conference-item__header mb-2">
                <p v-if="item.info?.is_live" class="mb-2 f-12 bold">{{ t('broadcastInProgress') }}</p>
                <p v-else-if="item.info?.is_over" class="mb-2 f-12 bold">{{ t('broadcasrFinish') }}</p>
                <template v-else>
                  <p class="mb-2 f-12 bold">
                    {{ t('translationStart') }}: {{ formatDate(item.info?.live_date) }}
                  </p>
                  <div class="d-flex align-items-center link-item--green">
                    <span class="material-icons mr-2 f-12 green-icon">access_time</span>
                    <span class="f-12">{{ item.info?.live_time }}</span>
                  </div>
                </template>
              </div>

              <div v-if="item.info?.welcome" class="conference-item__content mb-3">
                <p class="f-13 bold mb-2">{{ t('welcomeMessageWebinar') }}</p>
                <div class="f-13" v-html="item.info.welcome" />
              </div>

              <template v-if="item.info?.is_over !== null && !item.info?.is_live">
                <div v-if="!item.info?.records" class="f-14 bold" />
                <div v-else-if="Array.isArray(item.info.records) && !item.info.records.length" class="f-14 bold">
                  {{ t('recordEmpty') }}
                </div>
                <div v-else class="mb-3">
                  <iframe :src="item.info.records[0]" title="record" allowfullscreen />
                </div>
              </template>

              <div v-if="item.info?.presentation && item.info?.is_over" class="conference-item__footer my-3">
                <p class="f-13 bold mb-2">{{ t('dowloadFile') }}</p>
                <FileFilter
                  :url="item.info.presentation.url"
                  :name="item.info.presentation.name"
                  :file="item.info.presentation"
                  @open-file-viewer="openFrame"
                />
              </div>

              <h3 v-if="item.description" class="bold f-13 mb-2">{{ t('anotation') }}</h3>
              <div v-if="item.description" class="f-13 mb-2" v-html="item.description" />

              <div v-if="item.info?.is_live" class="task__footer broadcasts__btn">
                <button class="btn-custom f-13 conference-btn" type="button" @click="goBroadcast(item)">
                  {{ t('goToBroadcast') }}
                </button>
              </div>
            </div>
          </template>

          <template v-else-if="Number(item.type) === 6">
            <div class="conference-item">
              <div class="conference-item__header mb-2">
                <p class="mb-2 f-12 bold">
                  {{ t('broadcastStart') }}: {{ formatDate(item.info?.live_date) }}
                </p>
                <div class="d-flex align-items-center link-item--green">
                  <span class="material-icons mr-2 f-12 green-icon">access_time</span>
                  <span class="f-12">{{ item.info?.live_time }}</span>
                </div>
              </div>
              <div v-if="item.description" class="conference-item__content mb-3">
                <p class="f-13 bold mb-2">{{ t('anotation') }}</p>
                <div class="f-13" v-html="item.description" />
              </div>
              <button class="btn-custom f-13 conference-btn" type="button" @click="openUrl(item.info?.url)">
                {{ t('goToBroadcast') }}
              </button>
            </div>
          </template>
        </div>
      </article>
    </div>

    <FileViewer v-model="iframe" />
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import EmptyStateNotice from '@/components/common/EmptyStateNotice.vue';
import FileFilter from '@/components/common/FileFilter.vue';
import FileViewer from '@/components/common/FileViewer.vue';
import QueryErrorNotice from '@/components/common/QueryErrorNotice.vue';
import { joinSdotConference, useTaskMaterialsQuery } from '@/queries/task/useTaskQueries';

const props = defineProps({
  lessonId: {
    type: Number,
    default: 0,
  },
  roleId: {
    type: Number,
    default: 3,
  },
  pupilId: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['loading-change']);

const { t } = useI18n();

const opened = ref(new Set());
const iframe = ref({ open: false, url: '', type: '' });

const materialsQuery = useTaskMaterialsQuery(
  computed(() => Number(props.lessonId || 0)),
  computed(() => Number(props.roleId || 3)),
  computed(() => Number(props.pupilId || 0)),
  {
    retry: false,
  }
);

const isPending = computed(() => materialsQuery.isPending.value);
const materials = computed(() => (Array.isArray(materialsQuery.data.value) ? materialsQuery.data.value : []));
const materialsTabErrors = computed(() =>
  materialsQuery.isError.value ? [materialsQuery.error.value] : []
);

watch(
  () => materialsQuery.isFetching.value,
  (value) => emit('loading-change', value),
  { immediate: true }
);

watch(
  () => props.pupilId,
  () => {
    opened.value = new Set();
  }
);

function isOpen(index) {
  return opened.value.has(index);
}

function toggleItem(index) {
  if (opened.value.has(index)) opened.value.delete(index);
  else opened.value.add(index);
  opened.value = new Set(opened.value);
}

function getIcon(type) {
  const n = Number(type || 0);
  if (n === 1) return 'link';
  if (n === 2) return 'play_circle';
  if (n === 3) return 'description';
  if (n === 5 || n === 6) return 'videocam';
  return 'description';
}

function externalLink(item) {
  const link = item?.info?.external_link || item?.external_link || null;
  if (!link) return null;
  const url = String(link.name || link.url || '').trim();
  const name = String(link.text || link.name || '').trim();
  if (!url || !name) return null;
  return {
    url,
    name,
    file: {
      ...link,
      url,
      type: 'text/html',
      ext: 'html',
    },
  };
}

function materialFile(item) {
  const file = item?.info?.file || item?.file || null;
  if (!file?.url || !file?.name) return null;
  return {
    url: file.url,
    name: file.name,
    file,
  };
}

function videoInfo(item) {
  const video = item?.info?.video || item?.video || null;
  if (!video) return null;
  const provider = String(video.type || '').toLowerCase();
  const id = String(video.video_id || '').trim();
  if (!id) return null;
  return { provider, id };
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const locale = localStorage.getItem('translation') === 'kz' ? 'kk-KZ' : 'ru-RU';
  return date.toLocaleDateString(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function openUrl(url) {
  const next = String(url || '').trim();
  if (!next) return;
  window.open(next, '_blank', 'noopener,noreferrer');
}

function openFrame(file) {
  iframe.value = {
    open: true,
    url: String(file?.url || ''),
    type: String(file?.type || ''),
  };
}

async function goBroadcast(item) {
  try {
    const response = await joinSdotConference({
      conferenceId: Number(item?.id || 0),
      organizationId: item?.organization_id || item?.info?.organization_id || '',
    });
    const url = String(response?.url || '').trim();
    if (url) openUrl(url);
  } catch {
    // keep silent, old behavior was silent on failure
  }
}
</script>

<style scoped lang="scss">
.materials-list {
  margin: 0 8px;
}

.acc-item {
  width: 100%;
  border: none;
  background: #fff;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 8px;
  border-bottom: 1px solid #e0e0e0;
}

.collapse-title {
  color: #4f4f4f;
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
}

.icon {
  color: #36b453;
  font-size: 20px;
}

.acc-icon {
  color: #828282;
  transition: transform 0.2s ease;
}

.acc-icon.is-open {
  transform: rotate(180deg);
}

.material-body {
  padding: 8px;
  color: #4f4f4f;
}

iframe {
  width: 100%;
  border: 0;
  min-height: 240px;
}

.conference-item__footer {
  border-top: 1px dotted #bdbdbd;
  padding-top: 12px;
}

.broadcasts__btn {
  border-top: 1px dotted #bdbdbd;
  margin-top: 17px;
  padding-top: 17px;
}

.conference-btn {
  width: 100%;
  border: none;
  border-radius: 4px;
  background: #36b453;
  color: #fff;
  text-transform: uppercase;
  padding: 8px 12px;
}

.link-item--green .green-icon {
  color: #36b453;
}

@media screen and (min-width: 768px) {
  .materials-list {
    margin: 0 36px;
  }
}
</style>
